import React, { useState } from 'react';
import { Anchor, Box, Button, DataTable, Heading, Layer, Text } from 'grommet';
import { Close } from 'grommet-icons';
import PropTypes from 'prop-types';
import JobService from '../../api/job-service';
import CandidacyService from '../../api/candidacy-service';

export const GenerateMatchesButton = ({ job }) => {
    const [showModal, setShowModal] = useState(false);

    return (
        <Box>
            <Button
                primary
                size="large"
                label="Generate matches"
                onClick={() => setShowModal(true)}
            />
            {showModal && (
                <GenerateMatchesModal job={job} setShowModal={setShowModal} />
            )}
        </Box>
    );
};

const GenerateMatchesModal = ({ job, setShowModal }) => {
    const [buttonBusy, setButtonBusy] = useState();
    const [matchSuggestions, setMatchSuggestions] = useState();
    const [fetchError, setFetchError] = useState(false);
    const [createError, setCreateError] = useState(false);
    const [createdContactIds, setCreatedContactIds] = useState([]);

    const handleCreateResult = (contactId, failed) => {
        setCreateError(failed);
        if (!failed) setCreatedContactIds((ids) => [...ids, contactId]);
    };

    const generateMatches = async () => {
        setButtonBusy(true);
        let matches = null;
        try {
            matches = await new JobService().generateMatches(job.id);
        } catch (error) {
            console.log(error);
        }
        setMatchSuggestions(matches);
        setFetchError(matches === null);
        setButtonBusy(false);
    };

    return (
        <Layer
            onClickOutside={() => setShowModal(false)}
            onEsc={() => setShowModal(false)}
            position="center">
            <Box
                background="white"
                width="xlarge"
                pad="medium"
                round="6px"
                border={{ color: 'enrich-green-light', size: '3px' }}
                alignSelf="center"
                gap="30px">
                <Box direction="row" gap="20px" justify="between">
                    <Heading level="3" margin={{ vertical: 'none' }}>
                        Generate job match suggestions
                    </Heading>
                    <Box align="end">
                        <Close
                            onClick={() => {
                                setShowModal(false);
                            }}
                            cursor="pointer"
                        />
                    </Box>
                </Box>
                <Box>
                    <Box
                        width={{ min: 'medium' }}
                        align="center"
                        round="6px"
                        background="white">
                        <Text>
                            This will suggest matches based on the job&apos;s
                            weight fields. Weights with a value of 100 are
                            considered requirements and will not be displayed in
                            the score breakdown.
                        </Text>
                    </Box>
                </Box>

                {fetchError && (
                    <Box align="center">
                        <Text color="status-critical">
                            Could not load job matches. Please try again.
                        </Text>
                    </Box>
                )}

                {createError && (
                    <Box align="center">
                        <Text color="status-critical">
                            Could not create candidacy. Please try again.
                        </Text>
                    </Box>
                )}

                {matchSuggestions && (
                    <Box overflow="scroll">
                        <MatchSuggestions
                            matches={matchSuggestions}
                            job={job}
                            createdContactIds={createdContactIds}
                            onCreateResult={handleCreateResult}
                        />
                    </Box>
                )}

                <Box align="center">
                    <Button
                        primary
                        busy={buttonBusy}
                        label="Suggest matches"
                        style={{ width: '220px', height: '50px' }}
                        onClick={generateMatches}
                    />
                </Box>
            </Box>
        </Layer>
    );
};

const CreateCandidacyButton = ({ match, job, created, onCreateResult }) => {
    const [busy, setBusy] = useState(false);

    const handleCreate = async () => {
        setBusy(true);
        try {
            await new CandidacyService().createCandidacy({
                contact: match.id,
                job: job.id,
                jsonData: {
                    score: match.score,
                    scoreLog: match.scoreLog,
                },
            });
            onCreateResult(match.id, false);
        } catch (error) {
            console.log(error);
            onCreateResult(match.id, true);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Box>
            <Button
                busy={busy}
                label={created ? 'Created' : 'Create'}
                disabled={created}
                primary
                onClick={handleCreate}
            />
        </Box>
    );
};

// "Created" state lives in the modal because DataTable remounts its cells
// when the modal re-renders.
const MatchSuggestions = ({
    matches,
    job,
    createdContactIds,
    onCreateResult,
}) => {
    const dataColumns = [
        {
            header: 'Name',
            render: (match) => {
                return (
                    <Anchor
                        key={match.id}
                        href={`/contacts/${match.id}`}
                        label={match.name}
                    />
                );
            },
            property: 'name',
        },
        {
            header: 'Match Score',
            property: 'score',
        },
        {
            header: 'Score Breakdown',
            property: 'scoreLog',
        },
        {
            header: 'Action',
            property: 'id',
            render: (match) => (
                <CreateCandidacyButton
                    key={match.id}
                    match={match}
                    job={job}
                    created={createdContactIds.includes(match.id)}
                    onCreateResult={onCreateResult}
                />
            ),
        },
    ];

    return (
        <Box>
            <DataTable primaryKey="id" data={matches} columns={dataColumns} />
        </Box>
    );
};

GenerateMatchesButton.propTypes = {
    job: PropTypes.object,
};

GenerateMatchesModal.propTypes = {
    job: PropTypes.object,
    setShowModal: PropTypes.func,
};

CreateCandidacyButton.propTypes = {
    match: PropTypes.object,
    job: PropTypes.object,
    created: PropTypes.bool,
    onCreateResult: PropTypes.func,
};

MatchSuggestions.propTypes = {
    job: PropTypes.object,
    matches: PropTypes.array,
    createdContactIds: PropTypes.array,
    onCreateResult: PropTypes.func,
};
